import type { AnalysisSections, XmlItemRating } from "./analysis-types.js";

/*
 * Session quality on the RD Snapshot (NO-737, Shay's decision c157de60).
 *
 * A session's rating is the lower median of its rated Always List items. The
 * RD's badge is the lower median of the last 5 counted sessions' ratings, and
 * the trend compares it with the 5 before them. One module owns the rule, so
 * the analysis worker, the backfill script, and the RD detail read cannot
 * drift apart. Pure: no IO, no clock.
 */

/** The Always List item scale, worst to best. Order is the ranking. */
export const SESSION_RATINGS = [
  "needs_improvement",
  "not_yet",
  "meets",
  "great",
  "exceeds",
] as const;

export type SessionRating = (typeof SESSION_RATINGS)[number];

/** The decision's scale wording, used for the Snapshot badge and trend line. */
export const SESSION_RATING_LABELS: Record<SessionRating, string> = {
  needs_improvement: "Needs Improvement",
  not_yet: "Not there Yet",
  meets: "Meets",
  great: "Great",
  exceeds: "Exceeds",
};

/** Sessions in the badge window; the trend compares with the window before it. */
export const SESSION_QUALITY_WINDOW = 5;

/** Fewer counted sessions than this and the Snapshot shows no badge. */
export const SESSION_QUALITY_MINIMUM = 3;

export interface SessionQualityTrend {
  direction: "up" | "down" | "steady";
  /** The lower median of the 5 counted sessions before the window. */
  previousRating: SessionRating;
}

/** `GET /api/rds/:id` → `sessionQuality`; `null` when too few sessions count. */
export interface SessionQuality {
  rating: SessionRating;
  /** `null` until 5 counted sessions precede the window. */
  trend: SessionQualityTrend | null;
  /** Counted sessions in the window: 3 to 5. */
  sampleSize: number;
}

export function isSessionRating(value: unknown): value is SessionRating {
  return (SESSION_RATINGS as readonly unknown[]).includes(value);
}

function rank(rating: SessionRating): number {
  return SESSION_RATINGS.indexOf(rating);
}

/** The median by rank; an even count takes the lower of the two middles. */
export function lowerMedian(ratings: readonly SessionRating[]): SessionRating | null {
  if (ratings.length === 0) return null;

  const sorted = [...ratings].sort((left, right) => rank(left) - rank(right));

  return sorted[Math.floor((sorted.length - 1) / 2)] ?? null;
}

/**
 * The session's rating from its parsed analysis: the lower median of the
 * Always List items rated on the scale. Not-applicable and unknown ratings are
 * skipped; a missing or failed section, or no rated item, gives `null`, and a
 * `null` session does not count toward the badge.
 */
export function sessionRatingFromSections(
  sections: AnalysisSections
): SessionRating | null {
  const scorecard = sections.always_list_scorecard;
  if (!scorecard || "error" in scorecard) return null;

  const rated = scorecard.items
    .map((item): XmlItemRating => item.rating)
    .filter(isSessionRating);

  return lowerMedian(rated);
}

/**
 * The Snapshot summary from counted session ratings, newest first. Only the
 * first 10 are read: the 5-session window and the 5 before it.
 */
export function sessionQualityFromRatings(
  newestFirst: readonly SessionRating[]
): SessionQuality | null {
  const recent = newestFirst.slice(0, SESSION_QUALITY_WINDOW);
  const previous = newestFirst.slice(
    SESSION_QUALITY_WINDOW,
    SESSION_QUALITY_WINDOW * 2
  );
  const rating = lowerMedian(recent);
  if (rating === null || recent.length < SESSION_QUALITY_MINIMUM) return null;

  const previousRating =
    previous.length === SESSION_QUALITY_WINDOW ? lowerMedian(previous) : null;

  return {
    rating,
    trend: previousRating === null ? null : trendFrom(rating, previousRating),
    sampleSize: recent.length,
  };
}

function trendFrom(
  rating: SessionRating,
  previousRating: SessionRating
): SessionQualityTrend {
  const change = rank(rating) - rank(previousRating);
  const direction = change > 0 ? "up" : change < 0 ? "down" : "steady";

  return { direction, previousRating };
}
